import { useEffect, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { SessionStatus, selectStatus, useSession } from "@core/auth";
import type { UserVehicle } from "@spare-cart/contracts/vehicles";
import { apiClient } from "@core/api";
import {
  fetchShopForVehicle,
  pushShopForVehicle,
  toShopForVehicleSelectionFromApi,
} from "../hooks/shop-for-vehicle-api";
import {
  selectHydrateShopForVehicle,
  selectSetShopForVehicle,
  selectShopForVehicle,
  useShopForVehicle,
  useShopForVehicleHydrated,
} from "../hooks/useShopForVehicle";
import { toShopForVehicleSelection } from "../utils/shop-for-vehicle";

const pickGarageDefault = (vehicles: readonly UserVehicle[]): UserVehicle | null => {
  if (vehicles.length === 0) return null;
  const primary = vehicles.find((vehicle) => vehicle.isPrimary);
  if (primary !== undefined) return primary;
  if (vehicles.length === 1) {
    const only = vehicles[0];
    return only === undefined ? null : only;
  }
  return null;
};

export const ShopForVehicleSync = () => {
  const status = useSession(selectStatus);
  const user = useSession((state) => state.user);
  const hydrated = useShopForVehicleHydrated();
  const selection = useShopForVehicle(selectShopForVehicle);
  const hydrateFromServer = useShopForVehicle(selectHydrateShopForVehicle);
  const setShopForVehicle = useShopForVehicle(selectSetShopForVehicle);
  const reconciledServer = useRef(false);
  const seededGarage = useRef(false);

  const authenticated = hydrated && status === SessionStatus.AUTHENTICATED && user !== null;

  const remoteQuery = useQuery({
    queryKey: ["shop-for-vehicle", "remote"],
    queryFn: fetchShopForVehicle,
    enabled: authenticated,
    staleTime: Infinity,
  });

  const vehiclesQuery = useQuery({
    queryKey: ["user-vehicles", "shop-for-seed", user?.id],
    queryFn: () =>
      apiClient.list<UserVehicle>("/user-vehicles", {
        query: user === null ? {} : { userId: user.id },
      }),
    enabled: authenticated,
    staleTime: 60_000,
  });

  useEffect(() => {
    if (!authenticated || remoteQuery.data === undefined || reconciledServer.current) return;
    reconciledServer.current = true;

    if (remoteQuery.data !== null) {
      hydrateFromServer(toShopForVehicleSelectionFromApi(remoteQuery.data));
      return;
    }

    if (selection !== null) {
      void pushShopForVehicle(selection).catch(() => {});
    }
  }, [authenticated, hydrateFromServer, remoteQuery.data, selection]);

  useEffect(() => {
    if (!authenticated || !reconciledServer.current || seededGarage.current) return;
    if (remoteQuery.data !== null || selection !== null) return;
    if (vehiclesQuery.isPending || vehiclesQuery.data === undefined) return;

    const defaultVehicle = pickGarageDefault(vehiclesQuery.data.data);
    if (defaultVehicle === null) return;

    seededGarage.current = true;
    setShopForVehicle(toShopForVehicleSelection(defaultVehicle));
  }, [
    authenticated,
    remoteQuery.data,
    selection,
    setShopForVehicle,
    vehiclesQuery.data,
    vehiclesQuery.isPending,
  ]);

  return null;
};
